import {Table} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {useProjectId} from "../ProjectName";
import {ElevationReviewItem} from "../../../../api/ElevationTool";
import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip
} from "@mui/material";
import {grey} from "@mui/material/colors";
import {OpenInNew} from "@mui/icons-material";
import {DrwOutsideSizeCol} from "./DrwOutsideSizeCol";

// the drw elevation review screens (door/screen/hardware). Lists the drw-based elevations and
// opens the editor for one at its first linked opening in the matching workspace. The kind maps
// 1:1 to the editor workspace query param.
export type DrwElevationKind = "door" | "screen" | "hardware";

// fetch the review list for a kind. Shop-drawing scope only (no alternative).
function fetchReview(kind: DrwElevationKind, project: number) {
    switch(kind) {
        case "door": return api.elevationTool.reviewDoorElevations({project});
        case "screen": return api.elevationTool.reviewScreenElevations({project});
        case "hardware": return api.elevationTool.reviewHardwareElevations({project});
    }
}

export function ReviewDrwElevations(props: {kind: DrwElevationKind}) {
    const project = useProjectId();
    const nav = useNavigate();

    // shown when the user opens an elevation that no opening links (see openInEditor).
    const [noOpeningsWarning, setNoOpeningsWarning] = useState(false);

    // open the editor at the first opening that links this elevation. With no linked opening
    // there's no reference opening to load, so warn instead and require the user to link one.
    const openInEditor = (row: ElevationReviewItem) => {
        const first = row.openings[0];
        if(!first) {
            setNoOpeningsWarning(true);
            return;
        }

        nav(`/project/${project}/shop-drawing/elevation-tool?opening=${first.id}&workspace=${props.kind}`);
    };

    return (
        <>
            <Table<ElevationReviewItem>
                name={"drw-" + props.kind + "-elevations"}
                fetch={() => fetchReview(props.kind, project)}
                fetchDeps={[project, props.kind]}
                columns={[
                    {
                        name: "Name",
                        width: 250,
                        render: data => (
                            <div style={{display: "flex", alignItems: "center"}}>
                                <div style={{flex: 1}}>{data.name}</div>
                                <Tooltip title="Open in editor">
                                    <IconButton
                                        size="small"
                                        style={{color: grey["500"], padding: 2}}
                                        onClick={e => {
                                            e.stopPropagation();
                                            openInEditor(data);
                                        }}
                                    >
                                        <OpenInNew style={{fontSize: "0.9rem"}} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        ),
                    },
                    {
                        name: "Openings",
                        width: 120,
                        render: data => {
                            if(data.openings.length === 0) return "0";

                            return (
                                <Tooltip title={data.openings.map(o => o.name).join(", ")}>
                                    <span>{data.openings.length}</span>
                                </Tooltip>
                            );
                        },
                    },
                    props.kind === "screen" && DrwOutsideSizeCol(),
                ]}
            />

            <Dialog open={noOpeningsWarning} onClose={() => setNoOpeningsWarning(false)}>
                <DialogTitle>No openings linked</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This elevation is not linked to any opening. Link one or more openings to it in
                        the openings grid before you set it up in the editor.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNoOpeningsWarning(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
